import { Component, OnInit, input, OnDestroy, ViewEncapsulation, signal, effect, untracked, TemplateRef, ViewChild, Input } from '@angular/core';
import { CustomSQLQueryService } from '../../services/sql-queries.service';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '../../models/user';
import { CustomSQLQuery } from 'src/app/models/sql-query';
import { Integration } from '../../models/integration';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { ToastService } from 'src/app/services/toast.service';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { MatDialog } from '@angular/material/dialog';
import { query } from '@angular/animations';
import { createDeleteButton, createEditButton } from 'src/app/features/grid/classes/grid-config.types';

@Component({
  selector: 'app-sql-queries',
  templateUrl: './sql-queries.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./sql-queries.component.scss'],
  standalone: false,
})
export class SQLQueriesComponent implements OnInit, OnDestroy {
  @ViewChild('deleteSqlQueryDialog', { static: true }) deleteSqlQueryDialog: TemplateRef<any>;

  componentDestroyed$ = new Subject<boolean>();
  @Input() isInTab = false;
  integration = input<Integration>();
  @Input() client = null;

  // Constants
  customSqlQueries: CustomSQLQuery[];

  // Dynamic
  sqlQueryGridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);

  // Inline add/edit state (mirrors integration-pushback adding/editing flags)
  addingSqlQuery = false;
  editingSqlQuery = false;
  deletingSqlQuery = false;
  selectedQuery: CustomSQLQuery = null;

  constructor(
    protected sqlService: CustomSQLQueryService,
    private router: Router,
    private toastService: ToastService,
    private dialog: MatDialog,
    private user: User,
  ) {
    effect(() => {
      this.sqlQueryCancel();
      this.gridLoading.set(true);

      if (this.integration() != null) {
        untracked(() => this.getSqlQueriesByIntegration());
      } else {
        untracked(() => this.getAllSqlQueries());
      }
    });
  }

  ngOnInit(): void {
    this.sqlQueryGridSettings.set(this.setupGrid());
  }

  getAllSqlQueries(): void {
    this.sqlService.getAllCustomQueries()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (r) => {
          if (r != null) {
            this.customSqlQueries = r;
            this.sqlQueryGridSettings.set(this.sqlQueryGridSettings().setData(this.customSqlQueries));
          }
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to load SQL Queries.', 'Warning', {
            autoClose: false,
          });
        },
        complete: () => {
          this.sqlQueryGridSettings().loading.set(false);
        },
      });
  }

  getSqlQueriesByIntegration(): void {
    this.sqlService.getAllCustomQueriesByIntegration(this.integration().integrationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (r) => {
          if (r != null) {
            this.customSqlQueries = r;
            this.sqlQueryGridSettings.set(this.sqlQueryGridSettings().setData(this.customSqlQueries));
          }
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to load SQL Queries.', 'Warning', {
            autoClose: false,
          });
        },
        complete: () => {
          this.sqlQueryGridSettings().loading.set(false);
        },
      });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
  
  setEditingSqlQuery(selectedQuery: CustomSQLQuery): void {
    if (selectedQuery != null) {
      this.selectedQuery = selectedQuery;
      this.editingSqlQuery = true;
    }
  }

  setDeletingSqlQuery(selectedQuery: CustomSQLQuery): void {
    if (selectedQuery != null) {
      this.selectedQuery = selectedQuery;
      this.dialog.open(this.deleteSqlQueryDialog);
    }
  }

  checkAccess(role: string[]): boolean {
    if (this.user.checkAccess(role)) {
      return true;
    } else {
      return false;
    }
  }

  setupGrid(): GridSettings {
    var gridColumns = [
      new GridColumnData({ field: 'queryName', header: 'Query Name'}),
      new GridColumnData({ field: 'queryTypeName', header: 'Query Type'}),
    ];

    if (this.integration() == null) {
      gridColumns.push(
        new GridColumnData({ field: 'integrationSourceTypeId', header: 'Integration Type'}),
        new GridColumnData({ field: 'integrationName', header: 'Integration Name'}),
      );
    }

    return new GridSettings({
      loading: this.gridLoading,
      dataKey: 'customSQLQueryId',
      data: this.customSqlQueries,
      fixedGrid: true,
      columns: gridColumns,
      sortMode: 'multiple',
      filter: {
        enabled: true,
        display: 'row',
        mode: 'row',
      },
      multisort: [
        {
          field: 'queryName',
          order: -1,
        },
      ],
      commandsColumn: {
        enabled: true,
        headerText: 'Edit',
        layout: 'horizontal',
        buttonSpacing: '0.25rem',
        buttons: [
          createEditButton(),
          createDeleteButton(),
        ],
      },
    });
  }

  //#region - Inline Add / Edit (replaces router navigation)
  public goToAdd() {
    if (this.checkAccess(['LOSTalker.Admin'])) {
      this.selectedQuery = null;
      this.addingSqlQuery = true;
    }
  }

  sqlQueryAdded(_event: CustomSQLQuery): void {
    this.addingSqlQuery = false;
    this.selectedQuery = null;
    this.reloadQueries();
  }

  sqlQueryUpdated(_event: CustomSQLQuery): void {
    this.editingSqlQuery = false;
    this.selectedQuery = null;
    this.reloadQueries();
  }

  deleteSqlQuery(): void {
      this.deletingSqlQuery = true;
      this.sqlService.deleteCustomQuery(this.selectedQuery).subscribe({
          next: () => {
              this.customSqlQueries = this.customSqlQueries.filter(query => query.customSQLQueryId != this.selectedQuery.customSQLQueryId);
              this.toastService.toastCreate('Successfully Deleted ' + this.selectedQuery.queryTypeName + ' SQL Query (' + this.selectedQuery.queryName + ').', 'Success', {
                  autoClose: false,
              });
              this.sqlQueryGridSettings.set(this.sqlQueryGridSettings().setData(this.customSqlQueries));
          },
          error: (_error) => {
              this.toastService.toastCreate('Failed to delete ' + this.selectedQuery.queryTypeName + ' SQL Query (' + this.selectedQuery.queryName + ').', 'Danger', {
                  autoClose: false,   
              });
          },
          complete: () => {
              this.dialog.closeAll();
              this.deletingSqlQuery = false;
          }
      });
  }

  sqlQueryCancel(): void {
    this.addingSqlQuery = false;
    this.editingSqlQuery = false;
    this.selectedQuery = null;
  }

  private reloadQueries(): void {
    this.gridLoading.set(true);
    if (this.integration() != null) {
      this.getSqlQueriesByIntegration();
    } else {
      this.getAllSqlQueries();
    }
  }
  //#endregion

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
